import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../lib/api';
import Header from '../components/Header';
import SEO from '../components/SEO';
import { DEFAULT_CURRENCY, formatMoney } from '../components/marketplace/cartUtils';

interface BuyerOrderItem {
  id: number;
  title: string;
  commercial_quantity: number;
  commercial_unit: string;
  unit_price: number;
  currency: string;
  total_price: number;
  grade?: string | null;
}

interface SupplierOrder {
  id: number;
  order_number: string;
  status: string;
  supplier_name?: string;
  currency: string;
  total_amount: number;
}

interface BuyerOrder {
  id: number;
  order_number: string;
  status: string;
  currency: string;
  subtotal: number;
  total_amount: number;
  delivery_address?: string;
  delivery_date?: string;
  payment_terms?: string;
  notes?: string;
  buyer_name?: string;
  buyer_email?: string;
  buyer_org_name?: string;
  created_at: string;
  updated_at: string;
  item_count?: number;
  items: BuyerOrderItem[];
  supplierOrders: SupplierOrder[];
}

const statusColors: Record<string, string> = {
  awaiting_confirmation: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-blue-100 text-blue-800',
  stock_reserved: 'bg-cyan-100 text-cyan-800',
  preparing: 'bg-indigo-100 text-indigo-800',
  ready_for_quality_check: 'bg-indigo-100 text-indigo-800',
  quality_accepted: 'bg-teal-100 text-teal-800',
  in_transit: 'bg-purple-100 text-purple-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  buyer_accepted: 'bg-emerald-100 text-emerald-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
  disputed: 'bg-rose-100 text-rose-800',
  correction_required: 'bg-orange-100 text-orange-800',
};

const statusLabels: Record<string, string> = {
  awaiting_confirmation: 'Awaiting Confirmation',
  confirmed: 'Confirmed',
  stock_reserved: 'Stock Reserved',
  preparing: 'Preparing',
  ready_for_quality_check: 'Quality Check',
  quality_accepted: 'Quality Accepted',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  buyer_accepted: 'Accepted',
  completed: 'Completed',
  cancelled: 'Cancelled',
  disputed: 'Disputed',
  correction_required: 'Correction Needed',
};

const OrderHistoryPage: React.FC = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState<BuyerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/marketplace/buyer/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load orders');
      const data = await res.json();
      // The B2B endpoint returns { success: true, orders: [...] }
      setOrders((data.orders as BuyerOrder[]) || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filteredOrders = orders.filter(o =>
    filter === 'all' || o.status === filter
  );

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });

  // One formatter for the whole marketplace: "700,000 RWF", never a locale
  // symbol, so an order reads the same in a list, an email and an invoice.
  const money = (amount: number, currency = DEFAULT_CURRENCY) => formatMoney(amount || 0, currency);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="My Orders | eSanza Marketplace" description="View your order history and track deliveries" />
      <Header />

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">My Orders</h1>
            <p className="text-gray-500 mt-1">Track your purchases and deliveries</p>
          </div>
          <Link
            to="/buy"
            className="px-6 py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 shadow-lg shadow-emerald-200 transition-all"
          >
            Continue Shopping
          </Link>
        </div>

        {/* Filter Pills — use real B2B status vocabulary */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { key: 'all', label: 'All' },
            { key: 'awaiting_confirmation', label: 'Awaiting' },
            { key: 'confirmed', label: 'Confirmed' },
            { key: 'in_transit', label: 'In Transit' },
            { key: 'delivered', label: 'Delivered' },
            { key: 'completed', label: 'Completed' },
            { key: 'cancelled', label: 'Cancelled' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-xl text-sm font-bold capitalize transition-all ${
                filter === f.key
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200'
                  : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-emerald-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-500 mt-4 font-medium">Loading your orders...</p>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">⚠️</div>
            <p className="text-red-600 font-medium">{error}</p>
            <button onClick={fetchOrders} className="mt-4 text-emerald-600 font-bold hover:underline">Try again</button>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {filter === 'all' ? 'No orders yet' : `No ${filter} orders`}
            </h3>
            <p className="text-gray-500 mb-6">
              {filter === 'all'
                ? "Start shopping to see your orders here."
                : "Try a different filter."}
            </p>
            {filter === 'all' && (
              <Link
                to="/buy"
                className="inline-block px-6 py-3 rounded-xl font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 shadow-lg transition-all"
              >
                Browse Marketplace
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order: BuyerOrder) => {
              const latestSupplierOrder = order.supplierOrders?.[0];
              const statusColor = statusColors[order.status] || 'bg-gray-100 text-gray-800';
              const statusLabel = statusLabels[order.status] || order.status;
              return (
                <div key={order.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusColor}`}>
                          {statusLabel}
                        </span>
                        {order.supplierOrders && order.supplierOrders.length > 0 && (
                          <span className="text-xs text-gray-400">
                            {order.supplierOrders.length} supplier{order.supplierOrders.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <p className="text-lg font-bold text-gray-900">{order.order_number}</p>
                      {order.buyer_org_name && (
                        <p className="text-sm text-gray-500 mt-0.5">{order.buyer_org_name}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {formatDate(order.created_at)}
                        {order.delivery_date ? ` · Delivering ${formatDate(order.delivery_date)}` : ''}
                      </p>
                      {order.delivery_address && (
                        <p className="text-xs text-gray-400 mt-1 truncate max-w-md">📍 {order.delivery_address}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-extrabold text-gray-900">
                        {money(order.total_amount, order.currency)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {order.item_count || order.items?.length || 0} item{(order.item_count || order.items?.length || 0) !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  {/* Supplier order status strip */}
                  {latestSupplierOrder && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                      <span>Supply: {statusLabels[latestSupplierOrder.status] || latestSupplierOrder.status}</span>
                      {latestSupplierOrder.supplier_name && (
                        <span className="text-gray-300">·</span>
                      )}
                      {latestSupplierOrder.supplier_name && (
                        <span className="truncate">{latestSupplierOrder.supplier_name}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistoryPage;
